/**
 * Deterministic colour generation.
 *
 * Planets are not trying to look like our solar system. Any hue is fair game —
 * a violet ocean world or an orange jungle is a feature, not a mistake — with
 * only a mild pull toward the desaturated end so the galaxy does not read as a
 * bag of sweets.
 */
function channel(p, q, t) {
    let shifted = t;
    if (shifted < 0)
        shifted += 1;
    if (shifted > 1)
        shifted -= 1;
    if (shifted < 1 / 6)
        return p + (q - p) * 6 * shifted;
    if (shifted < 1 / 2)
        return q;
    if (shifted < 2 / 3)
        return p + (q - p) * (2 / 3 - shifted) * 6;
    return p;
}
/** `h`, `s` and `l` all in 0..1. */
export function hslToHex(h, s, l) {
    let r = l;
    let g = l;
    let b = l;
    if (s > 0) {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = channel(p, q, h + 1 / 3);
        g = channel(p, q, h);
        b = channel(p, q, h - 1 / 3);
    }
    const hex = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
}
/**
 * Saturation with a gentle bias toward muted.
 *
 * Squaring-ish the draw (exponent 1.6) leaves most worlds in the dusty range
 * while still letting a genuinely vivid one turn up. A stronger exponent made
 * everything grey; a linear draw made the map look like confetti.
 */
function mutedSaturation(rng, min = 0.08, max = 0.92) {
    const t = rng.float() ** 1.6;
    return min + t * (max - min);
}
/**
 * A planet's three-colour palette.
 *
 * The accent is a deliberate hue *step* rather than a shade of the base: a
 * planet whose land and sea differ only in brightness reads as a single blurry
 * ball at map distance. How far it steps is itself random, so some worlds are
 * subtle and some are garish.
 */
export function rollPlanetPalette(rng) {
    const hue = rng.float();
    const saturation = mutedSaturation(rng);
    const lightness = rng.range(0.3, 0.62);
    // Sometimes a neighbouring hue, sometimes most of the way round the wheel.
    const step = rng.bool(0.35) ? rng.range(0.25, 0.5) : rng.range(0.04, 0.16);
    const accentHue = (hue + (rng.bool() ? step : -step) + 1) % 1;
    return {
        base: hslToHex(hue, saturation, lightness),
        accent: hslToHex(accentHue, mutedSaturation(rng, 0.1, 0.95), rng.range(0.28, 0.68)),
        highlight: hslToHex((hue + rng.range(-0.06, 0.06) + 1) % 1, saturation * 0.5, rng.range(0.72, 0.94)),
    };
}
/**
 * Star colours, spanning red through pink, yellow, white and blue to green.
 *
 * Green stars do not exist in reality — a blackbody peaking in green still
 * looks white — but this is not a physics simulation, and a rare green sun is a
 * landmark players will remember and navigate by. Weighted so the ordinary
 * colours dominate and the strange ones stay worth finding.
 */
export const STAR_CLASSES = [
    'red', 'pink', 'orange', 'yellow', 'white', 'blue', 'green',
];
const STAR_SPECS = {
    red: { weight: 20, hue: [0.98, 1.03], saturation: [0.6, 0.9], lightness: [0.45, 0.6] },
    pink: { weight: 8, hue: [0.9, 0.97], saturation: [0.45, 0.8], lightness: [0.65, 0.8] },
    orange: { weight: 18, hue: [0.045, 0.09], saturation: [0.6, 0.95], lightness: [0.52, 0.66] },
    yellow: { weight: 22, hue: [0.11, 0.16], saturation: [0.5, 0.9], lightness: [0.6, 0.75] },
    white: { weight: 16, hue: [0.12, 0.62], saturation: [0.05, 0.2], lightness: [0.86, 0.96] },
    blue: { weight: 13, hue: [0.55, 0.63], saturation: [0.45, 0.85], lightness: [0.62, 0.8] },
    // The rarity is the point.
    green: { weight: 3, hue: [0.27, 0.38], saturation: [0.4, 0.75], lightness: [0.55, 0.72] },
};
export function rollStarColor(rng) {
    const starClass = rng.weighted(STAR_CLASSES.map((id) => [id, STAR_SPECS[id].weight]));
    const spec = STAR_SPECS[starClass];
    return {
        starClass,
        starColor: hslToHex((rng.range(spec.hue[0], spec.hue[1]) + 1) % 1, rng.range(spec.saturation[0], spec.saturation[1]), rng.range(spec.lightness[0], spec.lightness[1])),
    };
}
const MOON_GREYS = [0.03, 0.08, 0.55, 0.6, 0.12];
/**
 * A moon's palette: the same three roles a planet has, kept close together.
 *
 * Moons run through the planet shader now, and that shader needs somewhere to
 * put maria, crater floors and bright ejecta. But a moon given a planet's
 * palette stops reading as a moon — the whole point of the greys is that a
 * moon is a drab lump next to the world it orbits. So the accent and highlight
 * are steps in *lightness* around the base rather than steps in hue, which
 * gives the surface contrast to show its features without giving it a colour
 * scheme of its own.
 */
export function rollMoonPalette(rng) {
    const hue = rng.pick(MOON_GREYS);
    const saturation = rng.range(0.02, 0.18);
    const lightness = rng.range(0.38, 0.72);
    return {
        base: hslToHex(hue, saturation, lightness),
        // Maria and crater floors: darker, and allowed a touch more colour, since a
        // faintly rust or ochre basin is what stops a moon looking like a golf ball.
        accent: hslToHex((hue + rng.range(-0.04, 0.04) + 1) % 1, Math.min(0.3, saturation + rng.range(0, 0.12)), Math.max(0.08, lightness - rng.range(0.14, 0.3))),
        // Fresh ejecta and rims, which is the brightest thing on an airless body.
        highlight: hslToHex(hue, saturation * 0.6, Math.min(0.96, lightness + rng.range(0.16, 0.3))),
    };
}
//# sourceMappingURL=color.js.map